import {
  Cancel01Icon,
  SentIcon,
  Wrench01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Bubble, BubbleContent } from "@/components/ui/bubble";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Field, FieldError, FieldGroup } from "@/components/ui/field";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { Marker, MarkerContent } from "@/components/ui/marker";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "@/components/ui/message";
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from "@/components/ui/message-scroller";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type AgentChatItem =
  | {
      id: string;
      kind: "user" | "assistant" | "status";
      text: string;
      seq?: number;
    }
  | {
      id: string;
      kind: "tool";
      name: string;
      phase: "start" | "end";
      ok?: boolean;
      detail?: string;
      seq?: number;
    }
  | {
      id: string;
      kind: "error";
      text: string;
      seq?: number;
    };

export type CaseAgentChatProps = {
  versionId: string | null;
  liveStatus: string;
  items: AgentChatItem[];
  busy: boolean;
  onSend: (text: string) => boolean;
  onCancel: () => void;
};

function toolLabel(item: Extract<AgentChatItem, { kind: "tool" }>): string {
  if (item.phase === "start") return `${item.name}…`;
  if (item.ok === false) return `${item.name} 失败`;
  return `${item.name} 成功`;
}

export function CaseAgentChat({
  versionId,
  liveStatus,
  items,
  busy,
  onSend,
  onCancel,
}: CaseAgentChatProps) {
  const [draft, setDraft] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const canSend = Boolean(versionId) && liveStatus === "open" && !busy;

  const placeholder = useMemo(() => {
    if (!versionId) return "请先创建版本。";
    if (liveStatus !== "open") return "等待实时连接…";
    if (busy) return "Agent 正在处理…";
    return "让案例 Agent 读取或修改手机 JSON、列出资源、校验…";
  }, [versionId, liveStatus, busy]);

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    if (!canSend) {
      setLocalError(
        liveStatus !== "open"
          ? "实时连接未打开。"
          : busy
            ? "Agent 正忙。"
            : "没有版本。",
      );
      return;
    }
    setLocalError(null);
    const ok = onSend(text);
    if (!ok) {
      setLocalError("无法发送（连接已关闭）。");
      return;
    }
    setDraft("");
  };

  return (
    <Card className="flex min-h-[28rem] flex-col">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <CardTitle>案例 Agent</CardTitle>
            <CardDescription>
              通过与流水线、LLM 流相同的版本 WebSocket 对话。工具权限限定在本版本内。
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={
                liveStatus === "open"
                  ? "success"
                  : liveStatus === "error"
                    ? "destructive"
                    : liveStatus === "connecting"
                      ? "warning"
                      : "outline"
              }
            >
              ws: {liveStatus}
            </Badge>
            {busy ? <Badge variant="warning">运行中</Badge> : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="min-h-0 flex-1 rounded-lg border border-border">
          <MessageScrollerProvider autoScroll>
            <MessageScroller className="h-80">
              <MessageScrollerViewport>
                <MessageScrollerContent className="gap-4 p-3">
                  {items.length === 0 ? (
                    <Empty className="min-h-48 border-0">
                      <EmptyHeader>
                        <EmptyTitle>还没有 Agent 消息</EmptyTitle>
                        <EmptyDescription>
                          可以请求校验案例、修改字段或列出资源。重连后会回放本连接上的缓冲事件。
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  ) : (
                    items.map((item) => (
                      <MessageScrollerItem
                        key={item.id}
                        messageId={item.id}
                        scrollAnchor={item.kind === "user"}
                      >
                        {item.kind === "status" ? (
                          <Marker variant="separator">
                            <MarkerContent>
                              <span
                                className={cn(
                                  busy && item === items[items.length - 1]
                                    ? "shimmer"
                                    : undefined,
                                )}
                              >
                                {item.text}
                              </span>
                            </MarkerContent>
                          </Marker>
                        ) : item.kind === "tool" ? (
                          <Message align="start">
                            <MessageContent>
                              <MessageHeader>
                                <HugeiconsIcon
                                  icon={Wrench01Icon}
                                  data-icon="inline-start"
                                />
                                工具
                              </MessageHeader>
                              <Bubble
                                variant={
                                  item.ok === false ? "destructive" : "muted"
                                }
                                align="start"
                              >
                                <BubbleContent>
                                  <span className="font-medium">
                                    {toolLabel(item)}
                                  </span>
                                  {item.detail ? (
                                    <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap font-mono text-[0.65rem] text-muted-foreground">
                                      {item.detail}
                                    </pre>
                                  ) : null}
                                </BubbleContent>
                              </Bubble>
                            </MessageContent>
                          </Message>
                        ) : item.kind === "error" ? (
                          <Message align="start">
                            <MessageContent>
                              <Bubble variant="destructive" align="start">
                                <BubbleContent className="max-h-64 overflow-y-auto whitespace-pre-wrap">
                                  {item.text}
                                </BubbleContent>
                              </Bubble>
                            </MessageContent>
                          </Message>
                        ) : (
                          <Message
                            align={item.kind === "user" ? "end" : "start"}
                          >
                            <MessageContent>
                              <MessageHeader>
                                {item.kind === "user" ? "你" : "Agent"}
                              </MessageHeader>
                              <Bubble
                                variant={
                                  item.kind === "user" ? "default" : "secondary"
                                }
                                align={
                                  item.kind === "user" ? "end" : "start"
                                }
                              >
                                <BubbleContent className="max-h-64 overflow-y-auto whitespace-pre-wrap">
                                  {item.text}
                                </BubbleContent>
                              </Bubble>
                            </MessageContent>
                          </Message>
                        )}
                      </MessageScrollerItem>
                    ))
                  )}
                </MessageScrollerContent>
              </MessageScrollerViewport>
              <MessageScrollerButton />
            </MessageScroller>
          </MessageScrollerProvider>
        </div>

        <FieldGroup>
          <Field>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              className="max-h-40 overflow-y-auto"
              placeholder={placeholder}
              disabled={!versionId || busy}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </Field>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              disabled={!canSend || !draft.trim()}
              onClick={submit}
            >
              {busy ? (
                <Spinner />
              ) : (
                <HugeiconsIcon icon={SentIcon} data-icon="inline-start" />
              )}
              发送
            </Button>
            {busy ? (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
              >
                <HugeiconsIcon icon={Cancel01Icon} data-icon="inline-start" />
                取消
              </Button>
            ) : null}
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <KbdGroup>
                <Kbd>Cmd</Kbd>
                <span>/</span>
                <Kbd>Ctrl</Kbd>
                <span>+</span>
                <Kbd>Enter</Kbd>
              </KbdGroup>
              发送
            </span>
          </div>
          {localError ? <FieldError>{localError}</FieldError> : null}
        </FieldGroup>
      </CardContent>
    </Card>
  );
}

/** Reduce live agent_* events into chat items (also used on reconnect replay). */
export function reduceAgentChatEvent(
  prev: AgentChatItem[],
  event: {
    type: string;
    seq: number;
    run_id: string | null;
    call_id: string | null;
    payload: Record<string, unknown>;
  },
): { items: AgentChatItem[]; busy: boolean | null } {
  const phase = event.payload?.phase;
  // Pipeline refine_phone agent events show as tools/status; chat-phase
  // messages drive the transcript.
  const idBase = `${event.type}-${event.seq}`;

  if (event.type === "agent_delta") {
    const role = event.payload?.role;
    const text =
      typeof event.payload?.text === "string" ? event.payload.text : "";
    if (role === "user" && text) {
      return {
        items: [
          ...prev,
          { id: idBase, kind: "user", text, seq: event.seq },
        ],
        busy: true,
      };
    }
    if (role === "assistant" && text) {
      return {
        items: [
          ...prev,
          { id: idBase, kind: "assistant", text, seq: event.seq },
        ],
        busy: null,
      };
    }
    if (role === "status" && text) {
      return {
        items: [
          ...prev,
          { id: idBase, kind: "status", text, seq: event.seq },
        ],
        busy: true,
      };
    }
    // Non-chat pipeline agent_delta (refine_phone / generate_phone).
    if (
      phase === "refine_phone" ||
      phase === "enrich" ||
      phase === "generate_phone" ||
      phase === "build_phone"
    ) {
      const cycle =
        typeof event.payload?.cycle === "number"
          ? ` 第 ${event.payload.cycle} 轮`
          : typeof event.payload?.round === "number"
            ? ` 第 ${event.payload.round} 轮`
            : "";
      return {
        items: [
          ...prev,
          {
            id: idBase,
            kind: "status",
            text: `${String(phase)}${cycle}`,
            seq: event.seq,
          },
        ],
        busy: null,
      };
    }
    return { items: prev, busy: null };
  }

  if (event.type === "agent_tool") {
    const name =
      typeof event.payload?.name === "string"
        ? event.payload.name
        : "tool";
    const args =
      event.payload?.args && typeof event.payload.args === "object"
        ? JSON.stringify(event.payload.args).slice(0, 500)
        : undefined;
    return {
      items: [
        ...prev,
        {
          id: idBase,
          kind: "tool",
          name,
          phase: "start",
          detail: args,
          seq: event.seq,
        },
      ],
      busy: phase === "chat" ? true : null,
    };
  }

  if (event.type === "agent_tool_end") {
    const name =
      typeof event.payload?.name === "string"
        ? event.payload.name
        : "tool";
    const ok = event.payload?.ok !== false;
    const detail = event.payload?.error
      ? String(event.payload.error)
      : event.payload?.result != null
        ? JSON.stringify(event.payload.result).slice(0, 800)
        : undefined;
    return {
      items: [
        ...prev,
        {
          id: idBase,
          kind: "tool",
          name,
          phase: "end",
          ok,
          detail,
          seq: event.seq,
        },
      ],
      busy: null,
    };
  }

  if (event.type === "agent_done") {
    if (phase === "chat") {
      return { items: prev, busy: false };
    }
    return {
      items: [
        ...prev,
        {
          id: idBase,
          kind: "status",
          text: `Agent 完成（${String(phase ?? "pipeline")}）`,
          seq: event.seq,
        },
      ],
      busy: false,
    };
  }

  if (event.type === "agent_error") {
    const error =
      typeof event.payload?.error === "string"
        ? event.payload.error
        : "agent_error";
    return {
      items: [
        ...prev,
        { id: idBase, kind: "error", text: error, seq: event.seq },
      ],
      busy: false,
    };
  }

  return { items: prev, busy: null };
}
